import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import config from '../Config';
import Topbar from '../Components/Topbar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Dialog } from '@headlessui/react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import {
  formatEasternDate,
  formatEasternDateTime,
} from '../Utils/dateTime';
import { dollarsStringToCents, formatCentsAsCurrency } from '../Utils/money';
import AdminOrderFlowActions from '../Components/AdminOrderFlowActions';
import OrderPaymentsLedgerPanel from '../Components/OrderPaymentsLedgerPanel';
import { getContractorById, updateContractor } from '../Config/contractorService';
import { getOrderFinancialsMaster, getErrorMessage } from '../Config/adminOrderFlowService';

function getCustomerDisplayLabel(order) {
  const orgName =
    order?.organization_name?.trim() ||
    order?.organization?.name?.trim() ||
    order?.organization?.business_name?.trim() ||
    '';
  const customerName =
    order?.placed_by?.name?.trim() ||
    order?.placed_by?.business_name?.trim() ||
    order?.user?.name?.trim() ||
    '';
  const label = [orgName, customerName].filter(Boolean).join(', ');

  if (label) return label;

  return order?.user?.mobile?.trim() || 'N/A';
}

/** Normalize order `time_options` from API (ISO strings or rich objects). */
function collectOrderTimeOptionIsos(order) {
  const raw = order?.time_options ?? order?.timeOptions;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((opt) => {
      if (typeof opt === 'string') return opt.trim() || null;
      if (opt != null && typeof opt === 'object') {
        const v =
          opt.iso ??
          opt.value ??
          opt.scheduled_visit_at ??
          opt.scheduled_at ??
          opt.start;
        return typeof v === 'string' ? v.trim() || null : null;
      }
      return null;
    })
    .filter(Boolean);
}

function uniqueSortedIsos(isos) {
  return [...new Set(isos)].sort((a, b) => new Date(a) - new Date(b));
}

function isoTimesRoughlyEqual(a, b) {
  const ta = new Date(a).getTime();
  const tb = new Date(b).getTime();
  if (Number.isNaN(ta) || Number.isNaN(tb)) return String(a) === String(b);
  return ta === tb;
}

function getOrderPreferredTimeOptionsIsos(order) {
  return uniqueSortedIsos(collectOrderTimeOptionIsos(order));
}

/** True when we should surface a list (multi-slot or single slot that differs from expected start). */
function shouldShowOrderPreferredTimeOptions(order) {
  const isos = getOrderPreferredTimeOptionsIsos(order);
  if (isos.length === 0) return false;
  if (isos.length > 1) return true;
  const exp = order?.expected_started_at;
  if (!exp) return true;
  return !isoTimesRoughlyEqual(isos[0], exp);
}

function getAlternatePreferredTimeOptionIsos(order) {
  const isos = getOrderPreferredTimeOptionsIsos(order);
  const exp = order?.expected_started_at;
  if (!exp) return isos.slice(1);
  return isos.filter((iso) => !isoTimesRoughlyEqual(iso, exp));
}

/** Integer cents from API (*_cents / amount fields). */
function invoiceMoneyCents(value) {
  if (value == null) return 0;
  if (typeof value === 'number') return Math.round(value);
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n) : 0;
}

function financialsPaymentRows(master) {
  if (!master) return [];
  const p = master.payment;
  if (!p) return [];
  if (Array.isArray(p)) return p;
  if (Array.isArray(p.rows)) return p.rows;
  if (Array.isArray(p.payments)) return p.payments;
  if (p.id || p.stripe_payment_intent_id) return [p];
  return [];
}

function paymentRowId(row) {
  return row?.stripe_payment_intent_id ?? row?.payment_intent_id ?? row?.id ?? null;
}

function paymentRowStatus(row) {
  return (row?.status ?? row?.stripe_status ?? '').toUpperCase() || null;
}

function paymentRowAmountCents(row) {
  return invoiceMoneyCents(row?.amount_cents ?? row?.amount ?? 0);
}


/** Module-level so children are not remounted on every OrderDetails render. */
function CollapsibleSection({
  title,
  badge,
  open,
  onToggle,
  children,
  className = '',
  icon,
}) {
  return (
    <div className={`overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-sm ${className}`}>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggle();
          if (document.activeElement && typeof document.activeElement.blur === 'function') {
            document.activeElement.blur();
          }
        }}
        className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-gray-50/60"
      >
        <span className="flex items-center gap-2.5">
          {icon && <span className="text-gray-400 shrink-0">{icon}</span>}
          <span className="text-base font-semibold text-gray-900">{title}</span>
        </span>
        <span className="flex items-center gap-2">
          {badge != null ? (
            <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-500">{badge}</span>
          ) : null}
          <svg
            className={`h-4.5 w-4.5 h-5 w-5 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>
      {open ? <div className="border-t border-gray-100">{children}</div> : null}
    </div>
  );
}

function OrderDetails() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showUnassignModal, setShowUnassignModal] = useState(false);
  const [showApproveChargeModal, setShowApproveChargeModal] = useState(false);
  const [showRejectChargeModal, setShowRejectChargeModal] = useState(false);
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [adminRemark, setAdminRemark] = useState('');
  const [isApprovingCharge, setIsApprovingCharge] = useState(false);
  const [isRejectingCharge, setIsRejectingCharge] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [rescheduleDate, setRescheduleDate] = useState(new Date());
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [contractors, setContractors] = useState([]);
  const [selectedContractors, setSelectedContractors] = useState([]);
  const [chargePercent, setChargePercent] = useState(0);
  const [chargeAmount, setChargeAmount] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [notifySearchTerm, setNotifySearchTerm] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [commissionEditContractor, setCommissionEditContractor] = useState(null);
  const [commissionInput, setCommissionInput] = useState('');
  const [isSavingCommission, setIsSavingCommission] = useState(false);
  const [isUnassigning, setIsUnassigning] = useState(false);
  const [isNotifying, setIsNotifying] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [cancelChargePercent, setCancelChargePercent] = useState(0);
  const [cancelChargeAmount, setCancelChargeAmount] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [orderMedia, setOrderMedia] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [sectionOpen, setSectionOpen] = useState({});
  const [offlinePaymentRequest, setOfflinePaymentRequest] = useState(0);
  const [mediaFilter, setMediaFilter] = useState('all');
  const [mediaGroupBy] = useState('order_state');
  const [financialsMaster, setFinancialsMaster] = useState(null);
  const [financialsMasterLoading, setFinancialsMasterLoading] = useState(false);
  const [financialsMasterError, setFinancialsMasterError] = useState(null);


  const isSectionOpen = (id, defaultOpen = true) => (sectionOpen[id] ?? defaultOpen);
  const toggleSection = (id, defaultOpen = true) => setSectionOpen((prev) => ({ ...prev, [id]: !(prev[id] ?? defaultOpen) }));
  const sectionCollapsible = (id, defaultOpen = true) => ({
    open: isSectionOpen(id, defaultOpen),
    onToggle: () => toggleSection(id, defaultOpen),
  });

  const openOfflinePaymentForm = () => {
    setSectionOpen((prev) => ({ ...prev, payment: true }));
    setOfflinePaymentRequest((value) => value + 1);
    window.setTimeout(() => {
      document.getElementById('offline-payment-form')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 0);
  };

  const handleOfflinePaymentRecorded = async (ledger) => {
    await Promise.all([
      fetchOrderDetails({ silent: true }),
      loadFinancialsMaster(),
    ]);
    if (Number(ledger?.summary?.amount_remaining_cents) === 0) {
      setSectionOpen((prev) => ({ ...prev, 'admin-order-flow': true, payment: true }));
      window.setTimeout(() => {
        document.getElementById('admin-order-flow-section')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 0);
      toast.info('Payment is fully recorded. Review the order and click Complete order.');
    }
  };

  const fetchOrderDetails = useCallback(async (options = {}) => {
    const silent = Boolean(options.silent);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/v1/admin/orders/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setOrder(result.data);
        if (!silent) toast.success('Order details fetched successfully');
      } else {
        setError(result.message || 'Failed to fetch order details');
        if (!silent) toast.error(result.message || 'Failed to fetch order details');
      }
    } catch (err) {
      setError('An error occurred while fetching order details');
      if (!silent) toast.error('An error occurred while fetching order details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchContractors = useCallback(async () => {
    if (!id) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/v1/admin/orders/${id}/contractors`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (response.ok) {
        setContractors(data);
      } else {
        toast.error('Failed to fetch contractors');
      }
    } catch (error) {
      toast.error('Error fetching contractors');
    }
  }, [id]);

  const fetchOrderMedia = useCallback(async () => {
    if (!id) return;
    try {
      setMediaLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/v1/admin/orders/${id}/media`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const result = await response.json();
      if (response.ok && result.success && Array.isArray(result.data)) {
        setOrderMedia(result.data);
      } else {
        setOrderMedia([]);
      }
    } catch (err) {
      setOrderMedia([]);
    } finally {
      setMediaLoading(false);
    }
  }, [id]);

  // Load financials master invoice preview
  const loadFinancialsMaster = useCallback(async () => {
    if (!id) return;
    setFinancialsMasterLoading(true);
    setFinancialsMasterError(null);
    try {
      const { ok, body } = await getOrderFinancialsMaster(String(id));
      if (ok && body.success !== false) {
        setFinancialsMaster(body.data ?? body);
      } else {
        setFinancialsMaster(null);
        setFinancialsMasterError(getErrorMessage(body, 'Failed to load invoice preview'));
      }
    } catch (e) {
      setFinancialsMaster(null);
      setFinancialsMasterError(e?.message || 'Failed to load invoice preview');
    } finally {
      setFinancialsMasterLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fetchOrderDetails({ silent: true });
    fetchOrderMedia();
    fetchContractors();
    loadFinancialsMaster();
  }, [id, fetchOrderDetails, fetchOrderMedia, fetchContractors, loadFinancialsMaster]);

  const handleAssignContractor = async (contractorId) => {
    try {
      setIsAssigning(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/v1/admin/orders/${id}/assign-contractor`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ contractor_id: contractorId }),
      });

      if (response.ok) {
        toast.success('Contractor assigned successfully');
        setShowAssignModal(false);
        setSearchTerm('');
        fetchOrderDetails();
      } else {
        toast.error('Failed to assign contractor');
      }
    } catch (error) {
      toast.error('Error assigning contractor');
    } finally {
      setIsAssigning(false);
    }
  };

  const formatCommissionLabel = (value) => {
    if (value == null || value === '') return '—';
    const n = Number(value);
    return Number.isFinite(n) ? `${n.toFixed(2)}%` : '—';
  };

  const openCommissionEditor = (contractor) => {
    setCommissionEditContractor(contractor);
    const raw =
      contractor.commission_percentage != null && contractor.commission_percentage !== ''
        ? String(contractor.commission_percentage)
        : '';
    setCommissionInput(raw);
  };

  const handleSaveCommission = async () => {
    if (!commissionEditContractor?.contractor_id) return;
    const num = Number(commissionInput);
    if (commissionInput === '' || Number.isNaN(num) || num < 0 || num > 100) {
      toast.error('Enter a valid commission percentage between 0 and 100.');
      return;
    }
    setIsSavingCommission(true);
    try {
      const res = await getContractorById(commissionEditContractor.contractor_id);
      if (res.status !== 'success' || !res.data) {
        toast.error(res.message || 'Failed to load contractor');
        return;
      }
      const c = res.data;
      const formData = {
        name: c.user.name,
        mobile: c.user.mobile,
        email: c.user.email,
        gender: c.user.gender || '',
        profile_photo: c.user.profile_photo || '',
        age: c.user.age,
        active: c.active ? 'true' : 'false',
        commission_percentage: num,
      };
      const upd = await updateContractor(c.contractor_id, formData);
      if (upd.status === 'success') {
        toast.success('Commission updated');
        setCommissionEditContractor(null);
        await fetchContractors();
        await fetchOrderDetails({ silent: true });
      } else {
        toast.error(upd.message || 'Failed to update commission');
      }
    } catch (err) {
      toast.error('Error updating commission');
    } finally {
      setIsSavingCommission(false);
    }
  };

  const handleUnassignContractor = async () => {
    try {
      setIsUnassigning(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/v1/admin/orders/${id}/unassign-contractor`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        toast.success('Contractor unassigned successfully');
        fetchOrderDetails();
      } else {
        toast.error('Failed to unassign contractor');
      }
    } catch (error) {
      toast.error('Error unassigning contractor');
    } finally {
      setIsUnassigning(false);
    }
  };

  const handleNotifyContractors = async () => {
    try {
      setIsNotifying(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/v1/admin/orders/${id}/notify-contractors`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ contractor_ids: selectedContractors }),
      });

      if (response.ok) {
        toast.success('Contractors notified successfully');
        setShowNotifyModal(false);
        setSelectedContractors([]);
        setNotifySearchTerm('');
      } else {
        toast.error('Failed to notify contractors');
      }
    } catch (error) {
      toast.error('Error notifying contractors');
    } finally {
      setIsNotifying(false);
    }
  };

  const handleCancelOrder = async () => {
    if (Number(cancelChargePercent) > 0 && cancelChargeAmount !== '') {
      toast.error('Provide either charge percentage or charge amount, not both');
      return;
    }

    if (cancelChargeAmount !== '') {
      const amount = Number(cancelChargeAmount);
      if (Number.isNaN(amount) || amount < 0) {
        toast.error('Enter a valid charge amount in dollars');
        return;
      }
    } else {
      const percent = Number(cancelChargePercent);
      if (Number.isNaN(percent) || percent < 0 || percent > 100) {
        toast.error('Enter a valid charge percentage between 0 and 100');
        return;
      }
    }

    try {
      setIsCancelling(true);
      const token = localStorage.getItem('token');
      let chargeAmountCents = null;
      if (cancelChargeAmount !== '') {
        chargeAmountCents = dollarsStringToCents(cancelChargeAmount);
        if (Number.isNaN(chargeAmountCents) || chargeAmountCents < 0) {
          toast.error('Enter a valid charge amount in dollars');
          setIsCancelling(false);
          return;
        }
      }
      const response = await fetch(`${config.apiUrl}/api/v1/admin/orders/${id}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reason: cancelReason,
          charge_amount: chargeAmountCents,
          charge_percent: cancelChargePercent
        }),
      });

      if (response.ok) {
        toast.success('Order cancelled successfully');
        setShowCancelModal(false);
        setCancelReason('');
        setCancelChargePercent(0);
        setCancelChargeAmount('');
        fetchOrderDetails();
      } else {
        toast.error('Failed to cancel order');
      }
    } catch (error) {
      toast.error('Error cancelling order');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleRescheduleOrder = async () => {
    if (Number(chargePercent) > 0 && chargeAmount !== '') {
      toast.error('Provide either charge percentage or charge amount, not both');
      return;
    }

    if (chargeAmount !== '') {
      const amount = Number(chargeAmount);
      if (Number.isNaN(amount) || amount < 0) {
        toast.error('Enter a valid charge amount in dollars');
        return;
      }
    } else {
      const percent = Number(chargePercent);
      if (Number.isNaN(percent) || percent < 0 || percent > 100) {
        toast.error('Enter a valid charge percentage between 0 and 100');
        return;
      }
    }

    try {
      setIsRescheduling(true);
      const token = localStorage.getItem('token');
      let rescheduleChargeCents = null;
      if (chargeAmount !== '') {
        rescheduleChargeCents = dollarsStringToCents(chargeAmount);
        if (Number.isNaN(rescheduleChargeCents) || rescheduleChargeCents < 0) {
          toast.error('Enter a valid charge amount in dollars');
          setIsRescheduling(false);
          return;
        }
      }
      const response = await fetch(`${config.apiUrl}/api/v1/admin/orders/${id}/reschedule`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          new_start_time: rescheduleDate.toISOString(),
          reason: rescheduleReason,
          charge_amount: rescheduleChargeCents,
          charge_percent: chargePercent
        }),
      });

      if (response.ok) {
        toast.success('Order rescheduled successfully');
        setShowRescheduleModal(false);
        setRescheduleReason('');
        setChargeAmount('');
        setChargePercent(0);
        fetchOrderDetails();
      } else {
        toast.error('Failed to reschedule order');
      }
    } catch (error) {
      toast.error('Error rescheduling order');
    } finally {
      setIsRescheduling(false);
    }
  };

  const handleApproveCharge = async () => {
    try {
      setIsApprovingCharge(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/v1/admin/orders/${id}/charge/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          charge_id: selectedCharge.id,
          admin_remark: adminRemark
        }),
      });

      if (response.ok) {
        toast.success('Charge approved successfully');
        setShowApproveChargeModal(false);
        setAdminRemark('');
        setSelectedCharge(null);
        fetchOrderDetails();
      } else {
        toast.error('Failed to approve charge');
      }
    } catch (error) {
      toast.error('Error approving charge');
    } finally {
      setIsApprovingCharge(false);
    }
  };

  const handleRejectCharge = async () => {
    try {
      setIsRejectingCharge(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/v1/admin/orders/${id}/charge/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          charge_id: selectedCharge.id,
          admin_remark: adminRemark
        }),
      });

      if (response.ok) {
        toast.success('Charge rejected successfully');
        setShowRejectChargeModal(false);
        setAdminRemark('');
        setSelectedCharge(null);
        fetchOrderDetails();
      } else {
        toast.error('Failed to reject charge');
      }
    } catch (error) {
      toast.error('Error rejecting charge');
    } finally {
      setIsRejectingCharge(false);
    }
  };

  // Helper function to get creator's name and role
  const getCreatorInfo = (creatorId) => {
    if (!order) return { name: 'Unknown', role: 'Unknown' };

    if (order.user && order.user.user_id === creatorId) {
      return { name: order.user.name || 'User', role: 'User' };
    }

    if (order.contractor && order.contractor.user && order.contractor.user.user_id === creatorId) {
      return { name: order.contractor.user.name || 'Contractor', role: 'Contractor' };
    }

    if (order.worker && order.worker.user && order.worker.user.user_id === creatorId) {
      return { name: order.worker.user.name || 'Worker', role: 'Worker' };
    }

    return { name: 'Unknown', role: 'Unknown' };
  };

  // Add this helper function to filter contractors
  const getFilteredContractors = (contractors, searchTerm) => {
    return contractors.filter(contractor =>
      contractor.user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contractor.user.mobile?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Topbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-gray-200 border-t-indigo-600 mx-auto mb-4" />
            <p className="text-sm text-gray-500 font-medium">Loading order details…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Topbar />
        <div className="flex items-center justify-center min-h-[60vh] px-4">
          <div className="text-center max-w-sm">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <p className="text-red-600 text-lg font-semibold mb-1">Failed to load order</p>
            <p className="text-gray-500 text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const isCompleted = order && ['COMPLETE', 'CANCELED'].includes(order.status);
  const orderCurrency = order?.service?.currency || 'CAD';
  const preferredTimeIsos = order != null ? getOrderPreferredTimeOptionsIsos(order) : [];
  const showPreferredTimes = order != null && shouldShowOrderPreferredTimeOptions(order);
  const alternatePreferredIsos =
    order != null ? getAlternatePreferredTimeOptionIsos(order) : [];

  const statusGradient =
    order?.status === 'COMPLETE' ? 'bg-gradient-to-r from-emerald-400 to-emerald-600' :
    order?.status === 'CANCELED' ? 'bg-gradient-to-r from-red-400 to-red-600' :
    order?.status === 'IN_PROGRESS' || order?.status === 'DONE' ? 'bg-gradient-to-r from-blue-400 to-blue-600' :
    order?.status === 'PENDING' || order?.status === 'DRAFT' ? 'bg-gradient-to-r from-amber-400 to-amber-600' :
    'bg-gradient-to-r from-gray-300 to-gray-400';

  const statusBadgeClass =
    order?.status === 'COMPLETE' ? 'bg-emerald-100 text-emerald-800' :
    order?.status === 'CANCELED' ? 'bg-red-100 text-red-800' :
    order?.status === 'IN_PROGRESS' || order?.status === 'DONE' ? 'bg-blue-100 text-blue-800' :
    order?.status === 'PENDING' || order?.status === 'DRAFT' ? 'bg-amber-100 text-amber-800' :
    'bg-gray-100 text-gray-700';

  const logDotClass = (s) =>
    s === 'COMPLETE' ? 'bg-emerald-500' :
    s === 'CANCELED' ? 'bg-red-500' :
    s === 'IN_PROGRESS' || s === 'DONE' ? 'bg-blue-500' :
    s === 'PENDING' || s === 'DRAFT' ? 'bg-amber-500' :
    'bg-gray-400';

  const logBadgeClass = (s) =>
    s === 'COMPLETE' ? 'bg-emerald-100 text-emerald-700' :
    s === 'CANCELED' ? 'bg-red-100 text-red-700' :
    s === 'IN_PROGRESS' || s === 'DONE' ? 'bg-blue-100 text-blue-700' :
    s === 'PENDING' || s === 'DRAFT' ? 'bg-amber-100 text-amber-700' :
    'bg-gray-100 text-gray-600';

  return (
    <div className="min-h-screen bg-gray-50">
      <Topbar />
      <ToastContainer />
      <main className="w-full min-w-0 max-w-[1920px] mx-auto px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12 py-5 sm:py-6 lg:py-8">

        {/* ═══════════════════════════════════════
            HERO HEADER
        ═══════════════════════════════════════ */}
        <div className="rounded-2xl border border-gray-200/80 bg-white shadow-sm overflow-hidden mb-6">
          <div className={`h-1.5 w-full ${statusGradient}`} />
          <div className="p-5 sm:p-6 lg:p-8">

            {/* Title row + action buttons */}
            <div className="flex flex-wrap items-start gap-4 justify-between mb-6">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3 mb-1.5">
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
                    Order #{order?.order_id ?? id}
                  </h1>
                  {order?.status && (
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest ${statusBadgeClass}`}>
                      {order.status.replace(/_/g, ' ')}
                    </span>
                  )}
                </div>
                {order?.service?.title && (
                  <p className="text-gray-500 text-sm font-medium">
                    {order.service.title}
                    {order.service.sub_category && (
                      <span className="text-gray-400 ml-1.5">
                        · {order.service.sub_category.title}
                        {order.service.sub_category.category && ` / ${order.service.sub_category.category.title}`}
                      </span>
                    )}
                  </p>
                )}
                {order?.created_at && (
                  <p className="text-gray-400 text-xs mt-1">Created {formatEasternDateTime(order.created_at)}</p>
                )}
              </div>

              {/* Action buttons */}
              {order && !isCompleted && (
                <div className="flex flex-wrap gap-2 shrink-0">
                  {(order.status === 'DRAFT' || order.status === 'PENDING') && !order.contractor_id && (
                    <>
                      <button
                        onClick={() => setShowNotifyModal(true)}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-[0.98] transition-all shadow-sm"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>
                        Notify
                      </button>
                      <button
                        onClick={() => setShowAssignModal(true)}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 active:scale-[0.98] transition-all shadow-sm"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
                        Assign
                      </button>
                    </>
                  )}
                  {order.contractor_id && (
                    <button
                      onClick={() => setShowUnassignModal(true)}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 active:scale-[0.98] transition-all shadow-sm"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6" /></svg>
                      Unassign
                    </button>
                  )}
                  {!['IN_PROGRESS', 'DONE'].includes(order.status) && (
                    <button
                      onClick={() => setShowRescheduleModal(true)}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 active:scale-[0.98] transition-all shadow-sm"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                      Reschedule
                    </button>
                  )}
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 active:scale-[0.98] transition-all shadow-sm"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    Cancel
                  </button>
                </div>
              )}
            </div>

            {/* Key metrics strip */}
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {/* Amount */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Amount</p>
                {(() => {
                  const payCmp = financialsMaster?.payment?.comparison;
                  const invoiceTotal = payCmp?.amount_total_cents ?? payCmp?.invoice_total_cents ?? financialsMaster?.customer_invoice?.breakdown?.total_amount;
                  const hasInvoice = invoiceTotal != null && invoiceTotal > 0;
                  const displayCents = order?.amount ?? (hasInvoice ? invoiceMoneyCents(invoiceTotal) : null) ?? order?.expected_amount;
                  const isEstimate = order?.amount == null && !hasInvoice && order?.expected_amount != null;
                  return (
                    <>
                      <p className="text-xl sm:text-2xl font-bold text-gray-900">
                        {formatCentsAsCurrency(displayCents, orderCurrency)}
                      </p>
                      {isEstimate && (
                        <p className="text-xs text-gray-400 mt-0.5 italic">Expected (not yet final)</p>
                      )}
                      {!isEstimate && order?.amount == null && hasInvoice && (
                        <p className="text-xs text-emerald-600 mt-0.5 italic">Invoice total</p>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Customer */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Customer</p>
                <p className="text-sm font-semibold text-gray-900 truncate" title={getCustomerDisplayLabel(order)}>
                  {getCustomerDisplayLabel(order)}
                </p>
                {(order?.placed_by?.mobile || order?.user?.mobile) && (
                  <p className="text-xs text-gray-500 mt-0.5 font-mono">
                    {order?.placed_by?.mobile || order?.user?.mobile}
                  </p>
                )}
              </div>

              {/* Contractor */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Contractor</p>
                {order?.contractor ? (
                  <>
                    <p className="text-sm font-semibold text-gray-900 truncate" title={order.contractor.user?.name}>{order.contractor.user?.name || '—'}</p>
                    {order.contractor.user?.mobile && <p className="text-xs text-gray-500 mt-0.5 font-mono">{order.contractor.user.mobile}</p>}
                  </>
                ) : (
                  <p className="text-sm text-amber-600 font-semibold">Unassigned</p>
                )}
              </div>

              {/* Type + Payment */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Type · Payment</p>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold ${
                  order?.service?.service_type === 'HOUR' ? 'bg-blue-100 text-blue-800' :
                  order?.service?.service_type === 'QUOTATION' ? 'bg-amber-100 text-amber-800' :
                  order?.service?.service_type === 'AUTO' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {order?.service?.service_type || 'N/A'}
                </span>
                {order?.payment_method && (
                  <p className="text-xs text-gray-500 mt-1.5">via {order.payment_method}</p>
                )}
              </div>
            </div>

          </div>
        </div>

        {/* ═══════════════════════════════════════
            COLLAPSIBLE SECTIONS
        ═══════════════════════════════════════ */}
        <div className="space-y-4">

          {/* Admin Order Flow */}
          <div id="admin-order-flow-section">
            <CollapsibleSection
              title="Admin order flow"
              badge="Shortcuts"
              icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>}
              {...sectionCollapsible('admin-order-flow', false)}
            >
              <div className="p-5 sm:p-6">
                <AdminOrderFlowActions
                  key={id}
                  orderId={String(id)}
                  orderNumericId={order?.order_id}
                  customerUserId={order?.user?.user_id}
                  orderStatus={order?.status}
                  orderContractorId={order?.contractor_id}
                  orderWorker={order?.worker}
                  orderQuotation={order?.quotation}
                  orderTimeOptions={order ? getOrderPreferredTimeOptionsIsos(order) : []}
                  plannedJobVisitAt={
                    order?.planned_job_visit_at ??
                    order?.plannedJobVisitAt ??
                    order?.planned_job_visit?.scheduled_visit_at ??
                    null
                  }
                  service={order?.service}
                  disabled={isCompleted}
                  onRefresh={() => fetchOrderDetails({ silent: true })}
                  onRecordOfflinePayment={openOfflinePaymentForm}
                />
              </div>
            </CollapsibleSection>
          </div>

          {/* ─── ORDER DETAILS ─── */}
          {order && (
            <CollapsibleSection
              title="Order details"
              icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
              {...sectionCollapsible('order-details')}
            >
              <div className="p-5 sm:p-6 space-y-6">

                {/* ── Timing ── */}
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Timing</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3">
                    {[
                      { label: 'Created', value: formatEasternDateTime(order.created_at) },
                      { label: 'Updated', value: formatEasternDateTime(order.updated_at) },
                      { label: 'Expected start', value: order.expected_started_at ? formatEasternDateTime(order.expected_started_at) : 'N/A' },
                      { label: 'Expected end', value: order.expected_ended_at ? formatEasternDateTime(order.expected_ended_at) : 'N/A' },
                      { label: 'Actual start', value: order.started_at ? formatEasternDateTime(order.started_at) : 'N/A' },
                      { label: 'Actual end', value: order.ended_at ? formatEasternDateTime(order.ended_at) : 'N/A' },
                      { label: 'Expected duration', value: order.expected_time_taken != null ? `${order.expected_time_taken} min` : 'N/A' },
                      { label: 'Actual duration', value: order.time_taken ? `${order.time_taken} min` : 'N/A' },
                    ].map(({ label, value }) => (
                      <div key={label} className="bg-gray-50 rounded-xl p-3.5 border border-gray-100">
                        <p className="text-xs text-gray-400 font-medium mb-1">{label}</p>
                        <p className="text-sm font-semibold text-gray-800 leading-snug">{value}</p>
                      </div>
                    ))}
                  </div>
                  {showPreferredTimes && (
                    <div className="mt-3 bg-blue-50/60 rounded-xl p-4 border border-blue-100">
                      <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider mb-2">Preferred time options</p>
                      <div className="flex flex-wrap gap-2">
                        {preferredTimeIsos.map((iso) => (
                          <span key={iso} className="px-3 py-1 rounded-lg bg-blue-100 text-blue-800 text-sm font-medium">
                            {formatEasternDateTime(iso)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* ── Financials ── */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Financials</p>
                    <button
                      type="button"
                      onClick={loadFinancialsMaster}
                      disabled={financialsMasterLoading}
                      className="text-xs text-gray-400 hover:text-gray-700 underline disabled:opacity-50 transition-colors"
                    >
                      {financialsMasterLoading ? 'Refreshing…' : 'Refresh'}
                    </button>
                  </div>

                  {financialsMasterError && (
                    <p className="text-xs text-red-600 mb-3">{financialsMasterError}</p>
                  )}

                  {financialsMasterLoading && !financialsMaster && (
                    <div className="bg-gray-50 rounded-xl border border-gray-100 px-4 py-8 text-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-200 border-t-indigo-500 mx-auto" />
                      <p className="text-xs text-gray-400 mt-2">Loading invoice preview…</p>
                    </div>
                  )}

                  {financialsMaster && (() => {
                    const payCmp = financialsMaster?.payment?.comparison;
                    const totalDue = financialsMaster?.customer_invoice?.breakdown?.total_amount;
                    const lineItems = financialsMaster?.customer_invoice?.line_items ?? [];
                    const paymentRows = financialsPaymentRows(financialsMaster);
                    const pendingPreTax = financialsMaster?.charges?.pending_pre_tax_sum_cents;
                    const contractorLines = financialsMaster?.contractor_preview?.line_items ?? [];
                    const invoiceCurrency = financialsMaster?.meta?.currency ?? orderCurrency;
                    const moneyHint = financialsMaster?.meta?.display_money_hint;

                    return (
                      <div className="space-y-4">

                        {/* Invoice / Received / Remaining tiles */}
                        {payCmp && (typeof payCmp.amount_total_cents === 'number' || typeof payCmp.amount_received_cents === 'number' || typeof payCmp.amount_remaining_cents === 'number') ? (
                          <div className="grid grid-cols-3 gap-3">
                            {[
                              { label: 'Invoice', value: formatCentsAsCurrency(invoiceMoneyCents(payCmp.amount_total_cents ?? payCmp.invoice_total_cents ?? totalDue), invoiceCurrency), color: 'text-gray-900' },
                              { label: 'Received', value: formatCentsAsCurrency(invoiceMoneyCents(payCmp.amount_received_cents ?? 0), invoiceCurrency), color: 'text-emerald-700' },
                              { label: 'Remaining', value: formatCentsAsCurrency(invoiceMoneyCents(payCmp.amount_remaining_cents ?? 0), invoiceCurrency), color: 'text-amber-700' },
                            ].map(({ label, value, color }) => (
                              <div key={label} className="bg-gray-50 rounded-xl border border-gray-100 px-4 py-3">
                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
                                <p className={`text-lg font-bold tabular-nums ${color}`}>{value}</p>
                              </div>
                            ))}
                            {payCmp.note && (
                              <p className="col-span-3 text-xs text-gray-500 -mt-1">{payCmp.note}</p>
                            )}
                          </div>
                        ) : totalDue != null ? (
                          <div className="bg-gray-50 rounded-xl border border-gray-100 px-4 py-3 flex items-center justify-between">
                            <span className="text-sm text-gray-500">Amount due (invoice total)</span>
                            <span className="text-lg font-bold text-gray-900 tabular-nums">{formatCentsAsCurrency(invoiceMoneyCents(totalDue), invoiceCurrency)}</span>
                          </div>
                        ) : null}

                        {moneyHint && <p className="text-xs text-gray-500">{moneyHint}</p>}

                        {/* Stripe shortfall warning */}
                        {payCmp && typeof payCmp.shortfall_cents === 'number' && payCmp.shortfall_cents > 0 && (
                          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                            <p className="font-semibold">Stripe hold is below invoice total</p>
                            <p className="mt-0.5 text-xs">
                              Invoice {formatCentsAsCurrency(invoiceMoneyCents(payCmp.invoice_total_cents), invoiceCurrency)} ·
                              Card hold {formatCentsAsCurrency(invoiceMoneyCents(payCmp.authorized_cents), invoiceCurrency)} ·
                              Still needed {formatCentsAsCurrency(invoiceMoneyCents(payCmp.shortfall_cents), invoiceCurrency)}
                            </p>
                          </div>
                        )}

                        {/* Pending charges note */}
                        {pendingPreTax != null && (
                          <p className="text-xs text-amber-800">
                            Waiting on customer approval: {formatCentsAsCurrency(invoiceMoneyCents(pendingPreTax), invoiceCurrency)} (not added to total until accepted).
                          </p>
                        )}

                        {/* Line items — what the customer is charged */}
                        {lineItems.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">What the customer is charged</p>
                            <div className="bg-gray-50 rounded-xl border border-gray-100 overflow-hidden">
                              {lineItems.map((row, idx) => (
                                <div key={row.id ?? `li-${idx}`} className={`flex items-center justify-between px-4 py-3 ${idx < lineItems.length - 1 ? 'border-b border-gray-100' : ''}`}>
                                  <div className="min-w-0">
                                    <p className="text-sm text-gray-800">{row.label || row.title || row.description || '—'}</p>
                                    {row.kind && <p className="text-xs text-gray-400 font-mono mt-0.5">{row.kind}</p>}
                                  </div>
                                  <span className="text-sm font-bold text-gray-900 tabular-nums ml-4 shrink-0">
                                    {formatCentsAsCurrency(invoiceMoneyCents(row.amount_cents ?? row.amount ?? 0), invoiceCurrency)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Stripe payment rows */}
                        {paymentRows.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Stripe payments</p>
                            <div className="space-y-2">
                              {paymentRows.map((row, idx) => {
                                const pid = paymentRowId(row) || `payment-${idx}`;
                                const st = paymentRowStatus(row);
                                return (
                                  <div key={pid} className="bg-gray-50 rounded-xl border border-gray-100 px-4 py-3">
                                    <div className="flex items-center justify-between gap-3">
                                      <span className="font-mono text-xs text-gray-500 truncate">{pid}</span>
                                      <span className={`shrink-0 px-2 py-0.5 rounded-md text-xs font-semibold ${
                                        st === 'SUCCEEDED' ? 'bg-green-100 text-green-800' :
                                        st === 'CANCELED' ? 'bg-red-100 text-red-800' :
                                        'bg-gray-100 text-gray-700'
                                      }`}>{st || '—'}</span>
                                    </div>
                                    <p className="text-base font-bold text-gray-900 mt-1 tabular-nums">
                                      {formatCentsAsCurrency(paymentRowAmountCents(row), invoiceCurrency)}
                                    </p>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Contractor wallet preview */}
                        {contractorLines.length > 0 && (
                          <details className="rounded-xl border border-emerald-200 bg-emerald-50/40 px-4 py-3">
                            <summary className="cursor-pointer text-xs font-semibold text-emerald-800 uppercase tracking-wider">
                              Contractor wallet preview
                            </summary>
                            <div className="mt-3 space-y-2">
                              {contractorLines.map((row, idx) => (
                                <div key={idx} className="flex items-center justify-between text-xs text-emerald-900">
                                  <span>{row.label || row.description || row.kind || '—'}</span>
                                  <span className="font-semibold tabular-nums">
                                    {formatCentsAsCurrency(invoiceMoneyCents(row.amount_cents ?? row.amount ?? 0), invoiceCurrency)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </details>
                        )}

                      </div>
                    );
                  })()}

                  {!financialsMasterLoading && !financialsMaster && !financialsMasterError && (
                    <div className="bg-gray-50 rounded-xl border border-gray-100 px-4 py-6 text-center">
                      <p className="text-xs text-gray-400">No invoice preview available.</p>
                    </div>
                  )}
                </div>


                {/* ── People ── */}
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">People</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

                    {/* Customer */}
                    <div className="bg-blue-50/50 rounded-xl border border-blue-100 p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-blue-200 flex items-center justify-center text-blue-800 font-bold text-sm shrink-0">
                          {order.user?.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider">Customer</p>
                          <p className="text-sm font-bold text-gray-900">{getCustomerDisplayLabel(order)}</p>
                        </div>
                      </div>
                      <div className="space-y-1 text-xs mb-3">
                        {(order.placed_by?.mobile || order.user?.mobile) && (
                          <p className="font-mono text-gray-600">{order.placed_by?.mobile || order.user?.mobile}</p>
                        )}
                        {(order.placed_by?.email || order.user?.email) && (
                          <p className="text-gray-500 truncate">{order.placed_by?.email || order.user?.email}</p>
                        )}
                      </div>
                      {order.address && (
                        <div className="rounded-lg bg-white border border-blue-100 px-3 py-2.5">
                          <div className="flex items-start gap-2">
                            <svg className="w-3.5 h-3.5 text-blue-500 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-800 leading-snug">
                                {order.address.line1}
                                {order.address.line2 && `, ${order.address.line2}`}
                              </p>
                              {(order.address.area || order.address.city || order.address.pincode) && (
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {[order.address.area, order.address.city, order.address.pincode].filter(Boolean).join(', ')}
                                </p>
                              )}
                              {order.address.label && (
                                <span className="inline-block mt-1.5 px-2 py-0.5 rounded-md bg-blue-100 text-blue-700 text-xs font-semibold">
                                  {order.address.label}
                                </span>
                              )}
                              {(order.address.latitude || order.address.longitude) && (
                                <p className="text-xs text-gray-400 mt-1 font-mono">{order.address.latitude}, {order.address.longitude}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Contractor */}
                    {order.contractor ? (
                      <div className="bg-green-50/50 rounded-xl border border-green-100 p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-full bg-green-200 flex items-center justify-center text-green-800 font-bold text-sm shrink-0">
                            {order.contractor.user?.name?.charAt(0)?.toUpperCase() || 'C'}
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-green-600 uppercase tracking-wider">Contractor</p>
                            <p className="text-sm font-bold text-gray-900">{order.contractor.user?.name || '—'}</p>
                          </div>
                        </div>
                        <div className="space-y-1 text-xs">
                          {order.contractor.user?.mobile && <p className="font-mono text-gray-600">{order.contractor.user.mobile}</p>}
                          {order.contractor.user?.email && <p className="text-gray-500 truncate">{order.contractor.user.email}</p>}
                          {order.contractor.user?.business_name && <p className="text-gray-500">{order.contractor.user.business_name}</p>}
                          <div className="flex flex-wrap items-center gap-2 pt-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold ${
                              order.contractor.verification_status === 'VERIFIED' ? 'bg-green-100 text-green-800' :
                              order.contractor.verification_status === 'REJECTED' || order.contractor.verification_status === 'SUSPENDED' ? 'bg-red-100 text-red-800' :
                              order.contractor.verification_status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {order.contractor.verification_status}
                            </span>
                            <span className="text-gray-600">
                              Commission: <span className="font-semibold">{formatCommissionLabel(order.contractor.commission_percentage)}</span>
                            </span>
                            <button
                              type="button"
                              onClick={() => openCommissionEditor({
                                contractor_id: order.contractor_id,
                                user: order.contractor.user,
                                commission_percentage: order.contractor.commission_percentage,
                              })}
                              className="text-green-700 underline hover:text-green-900 font-semibold"
                            >
                              Edit
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-50 rounded-xl border border-dashed border-gray-200 p-4 flex items-center justify-center min-h-[120px]">
                        <p className="text-sm text-gray-400 italic">No contractor assigned</p>
                      </div>
                    )}

                    {/* Worker */}
                    {order.worker && (
                      <div className="bg-purple-50/50 rounded-xl border border-purple-100 p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-full bg-purple-200 flex items-center justify-center text-purple-800 font-bold text-sm shrink-0">
                            {order.worker.user?.name?.charAt(0)?.toUpperCase() || 'W'}
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-purple-600 uppercase tracking-wider">Worker</p>
                            <p className="text-sm font-bold text-gray-900">{order.worker.user?.name || '—'}</p>
                          </div>
                        </div>
                        <div className="space-y-1 text-xs">
                          {order.worker.user?.mobile && <p className="font-mono text-gray-600">{order.worker.user.mobile}</p>}
                          {order.worker.user?.email && <p className="text-gray-500 truncate">{order.worker.user.email}</p>}
                          <div className="pt-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold ${
                              order.worker.verification_status === 'VERIFIED' ? 'bg-green-100 text-green-800' :
                              order.worker.verification_status === 'REJECTED' || order.worker.verification_status === 'SUSPENDED' ? 'bg-red-100 text-red-800' :
                              order.worker.verification_status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {order.worker.verification_status}
                            </span>
                          </div>
                          {order.worker.contractor && (
                            <p className="text-gray-500 pt-1">
                              Under: {order.worker.contractor.user?.name || '—'}
                              {order.worker.contractor.user?.business_name && ` (${order.worker.contractor.user.business_name})`}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Remarks ── */}
                {(order.user_remark || order.worker_remark) && (
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Remarks</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {order.user_remark && (
                        <div className="bg-yellow-50/60 rounded-xl border border-yellow-100 p-4">
                          <p className="text-xs font-semibold text-yellow-700 uppercase tracking-wider mb-2">User remark</p>
                          <p className="text-sm text-gray-700">{order.user_remark}</p>
                        </div>
                      )}
                      {order.worker_remark && (
                        <div className="bg-orange-50/60 rounded-xl border border-orange-100 p-4">
                          <p className="text-xs font-semibold text-orange-700 uppercase tracking-wider mb-2">Worker remark</p>
                          <p className="text-sm text-gray-700">{order.worker_remark}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Preferences ── */}
                {order.preferences && order.preferences.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Preferences</p>
                    <div className="flex flex-wrap gap-2">
                      {order.preferences.map((pref, idx) => (
                        <span key={idx} className="px-3 py-1.5 rounded-lg bg-indigo-50 border border-indigo-100 text-sm text-indigo-800 font-medium">
                          {pref}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Images ── */}
                {(() => {
                  const imageSections = [
                    { key: 'user_images', label: 'User images', images: order.user_images },
                    { key: 'worker_images_before', label: 'Worker — before', images: order.worker_images_before },
                    { key: 'worker_images_after', label: 'Worker — after', images: order.worker_images_after },
                    { key: 'worker_images', label: 'Worker images', images: order.worker_images },
                    { key: 'material_images', label: 'Material images', images: order.material_images },
                  ].filter((s) => s.images && s.images.length > 0);
                  if (imageSections.length === 0) return null;
                  return (
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Images</p>
                      <div className="space-y-4">
                        {imageSections.map(({ key, label, images }) => (
                          <div key={key}>
                            <p className="text-xs font-semibold text-gray-500 mb-2">{label}</p>
                            <div className="flex flex-wrap gap-3">
                              {images.map((img, idx) => (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={() => { setSelectedImage(img); setShowImageModal(true); }}
                                  className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl overflow-hidden border border-gray-200 hover:border-gray-400 hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                  <img src={img} alt={`${label} ${idx + 1}`} className="w-full h-full object-cover" />
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* ── Vehicle (AUTO) ── */}
                {order.service?.service_type === 'AUTO' && (
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Vehicle details</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                      {[
                        { label: 'Company', value: order.company || 'N/A' },
                        { label: 'Model', value: order.model || 'N/A' },
                        { label: 'Year', value: order.build_year || 'N/A' },
                        { label: 'Color', value: order.colour || 'N/A' },
                        { label: 'VIN / Reg', value: order.vin_no || 'N/A' },
                      ].map(({ label, value }) => (
                        <div key={label} className="bg-gray-50 rounded-xl p-3.5 border border-gray-100">
                          <p className="text-xs text-gray-400 font-medium mb-1">{label}</p>
                          <p className="text-sm font-semibold text-gray-800">{value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Quotation (QUOTATION) ── */}
                {order.service?.service_type === 'QUOTATION' && (
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Quotation details</p>
                    {order.quotation && order.quotation.length > 0 ? (
                      <div className="space-y-3">
                        {order.quotation.map((quote, quoteIdx) => (
                          <div key={quote.quotation_id ?? quote.qotation_id ?? quoteIdx} className="bg-amber-50/60 rounded-xl border border-amber-100 p-4">
                            <div className="flex flex-wrap items-center gap-3 mb-3">
                              <span className="text-lg font-bold text-gray-900">{formatCentsAsCurrency(quote.amount, orderCurrency)}</span>
                              <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${
                                quote.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                                quote.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                                quote.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                                'bg-blue-100 text-blue-800'
                              }`}>{quote.status}</span>
                              <span className="text-xs text-gray-400">#{quote.qotation_id || quote.quotation_id}</span>
                            </div>
                            <div className="text-xs text-gray-500 space-y-1">
                              <p>Created: {formatEasternDateTime(quote.created_at)}</p>
                              <p>Updated: {formatEasternDateTime(quote.updated_at)}</p>
                              {quote.details && <p className="text-gray-700 mt-1">{quote.details}</p>}
                              {quote.user_remark && <p>User remark: {quote.user_remark}</p>}
                            </div>
                            {quote.images && quote.images.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-3">
                                {quote.images.map((img, idx) => (
                                  <button key={idx} type="button"
                                    onClick={() => { setSelectedImage(img); setShowImageModal(true); }}
                                    className="w-20 h-20 rounded-lg overflow-hidden border border-gray-200 hover:shadow-md transition-all focus:outline-none">
                                    <img src={img} alt={`Quote ${idx + 1}`} className="w-full h-full object-cover" />
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic">No quotation submitted yet.</p>
                    )}
                  </div>
                )}

                {/* ── Charges ── */}
                {order.charges && order.charges.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Charges</p>
                    <div className="space-y-3">
                      {order.charges.map((charge) => (
                        <div key={charge.id} className="bg-indigo-50/50 rounded-xl border border-indigo-100 p-4">
                          <div className="flex flex-wrap items-center gap-3 mb-2">
                            <span className="text-base font-bold text-gray-900">{formatCentsAsCurrency(charge.amount, orderCurrency)}</span>
                            <span className="text-sm text-gray-600 font-medium">{charge.charge_type}</span>
                            <span className={`inline-flex px-2.5 py-1 rounded-lg text-xs font-semibold ${
                              charge.status === 'ADMIN_APPROVED' ? 'bg-emerald-100 text-emerald-800' :
                              charge.status === 'PENDING' ? 'bg-amber-100 text-amber-800' :
                              charge.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                              charge.status === 'CANCELED' ? 'bg-gray-100 text-gray-700' :
                              'bg-blue-100 text-blue-800'
                            }`}>{charge.status}</span>
                            <span className="text-xs text-gray-400">{formatEasternDateTime(charge.created_at)}</span>
                          </div>
                          <div className="text-xs text-gray-600 space-y-0.5">
                            {charge.remark && <p>Remark: {charge.remark}</p>}
                            {charge.admin_remark && <p>Admin: {charge.admin_remark}</p>}
                            {charge.user_remark && <p>User: {charge.user_remark}</p>}
                          </div>
                          {charge.images && charge.images.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              {charge.images.map((img, idx) => (
                                <button key={idx} type="button"
                                  onClick={() => { setSelectedImage(img); setSelectedCharge(charge); setShowImageModal(true); }}
                                  className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200 hover:shadow-md transition-all focus:outline-none">
                                  <img src={img} alt={`Charge img ${idx + 1}`} className="w-full h-full object-cover" />
                                </button>
                              ))}
                            </div>
                          )}
                          {!['COMPLETE', 'DONE', 'CANCELED'].includes(order.status) && (
                            <div className="flex gap-2 mt-3 pt-3 border-t border-indigo-100/80">
                              {charge.status === 'PENDING' && (
                                <button onClick={() => { setSelectedCharge(charge); setShowApproveChargeModal(true); }}
                                  className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition-colors">
                                  Approve
                                </button>
                              )}
                              {['PENDING', 'ADMIN_APPROVED', 'ACCEPTED'].includes(charge.status) && (
                                <button onClick={() => { setSelectedCharge(charge); setShowRejectChargeModal(true); }}
                                  className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700 transition-colors">
                                  Reject
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Extra charges ── */}
                {order.extra_charge && order.extra_charge.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Extra charges</p>
                    <p className="text-lg font-bold text-gray-800 mb-3">
                      {formatCentsAsCurrency(order.extra_charge.reduce((s, c) => s + (c.charge_amount || 0), 0), orderCurrency)}
                      <span className="text-sm font-normal text-gray-400 ml-2">· {order.extra_charge.length} item{order.extra_charge.length !== 1 ? 's' : ''}</span>
                    </p>
                    <div className="space-y-2">
                      {order.extra_charge.map((charge) => {
                        const initiator = getCreatorInfo(charge.created_by);
                        return (
                          <div key={charge.id} className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 bg-gray-50/50 hover:shadow-sm transition-all">
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-900 text-sm">{charge.reason}</p>
                              <div className="flex flex-wrap items-center gap-2 mt-1">
                                <span className="text-xs px-2 py-0.5 rounded-md bg-gray-200 text-gray-700 font-medium">{charge.charge_type}</span>
                                <span className="text-xs text-gray-400">{formatEasternDateTime(charge.created_at)}</span>
                                <span className="text-xs text-gray-500">
                                  {initiator.name} <span className="text-gray-400">({initiator.role})</span>
                                </span>
                              </div>
                            </div>
                            <p className="text-base font-bold text-gray-900 shrink-0">{formatCentsAsCurrency(charge.charge_amount, orderCurrency)}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              </div>
            </CollapsibleSection>
          )}

          {/* ─── ORDER MEDIA ─── */}
          {order && (
            <CollapsibleSection
              title="Order media"
              badge={!mediaLoading && orderMedia.length > 0 ? orderMedia.length : null}
              icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>}
              {...sectionCollapsible('order-media')}
            >
              <div className="p-5 sm:p-6">
                {orderMedia.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 mb-5">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Filter:</span>
                    {['all', 'image', 'video', 'file'].map((key) => {
                      const count = key === 'all' ? orderMedia.length : key === 'image' ? orderMedia.filter((m) => (m.mimeType || '').startsWith('image/')).length : key === 'video' ? orderMedia.filter((m) => (m.mimeType || '').startsWith('video/')).length : orderMedia.filter((m) => !(m.mimeType || '').startsWith('image/') && !(m.mimeType || '').startsWith('video/')).length;
                      const active = mediaFilter === key;
                      return (
                        <button key={key} type="button" onClick={() => setMediaFilter(key)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${active ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                          {key === 'all' ? 'All' : key === 'image' ? 'Images' : key === 'video' ? 'Videos' : 'Files'} ({count})
                        </button>
                      );
                    })}
                  </div>
                )}
                {mediaLoading ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} className="rounded-xl overflow-hidden bg-gray-100 animate-pulse">
                        <div className="aspect-square w-full" />
                        <div className="p-3 space-y-2">
                          <div className="h-3 bg-gray-200 rounded w-3/4" />
                          <div className="flex gap-2"><div className="h-5 bg-gray-200 rounded w-16" /><div className="h-5 bg-gray-200 rounded w-14" /></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : orderMedia.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 px-4 rounded-xl bg-gray-50 border border-dashed border-gray-200">
                    <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center mb-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    </div>
                    <p className="text-gray-600 font-medium">No media for this order</p>
                    <p className="text-sm text-gray-400 mt-1">Photos or files will appear here when uploaded</p>
                  </div>
                ) : (() => {
                  const filtered = orderMedia.filter((m) => {
                    if (mediaFilter === 'all') return true;
                    if (mediaFilter === 'image') return (m.mimeType || '').startsWith('image/');
                    if (mediaFilter === 'video') return (m.mimeType || '').startsWith('video/');
                    return !(m.mimeType || '').startsWith('image/') && !(m.mimeType || '').startsWith('video/');
                  });
                  const groups = filtered.reduce((acc, item) => {
                    const key = item.order_state || 'Other';
                    if (!acc[key]) acc[key] = [];
                    acc[key].push(item);
                    return acc;
                  }, {});
                  const groupKeys = Object.keys(groups).sort((a, b) => {
                    const order = ['IN_PROGRESS', 'DONE', 'COMPLETE', 'Other'];
                    return (order.indexOf(a) === -1 ? 99 : order.indexOf(a)) - (order.indexOf(b) === -1 ? 99 : order.indexOf(b));
                  });
                  return (
                    <div className="space-y-6">
                      {groupKeys.map((groupKey) => {
                        const items = groups[groupKey];
                        const isImage = (m) => (m.mimeType || '').startsWith('image/');
                        const isVideo = (m) => (m.mimeType || '').startsWith('video/');
                        const fileSize = (item) => item.size == null ? null : item.size < 1024 ? `${item.size} B` : item.size < 1024 * 1024 ? `${(item.size / 1024).toFixed(1)} KB` : `${(item.size / (1024 * 1024)).toFixed(1)} MB`;
                        return (
                          <div key={groupKey}>
                            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                              <span className="bg-gray-200 text-gray-700 px-2 py-0.5 rounded">{groupKey}</span>
                              <span className="text-gray-400 font-normal">({items.length})</span>
                            </h3>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                              {items.map((item) => (
                                <div key={item.id} className="group rounded-xl overflow-hidden border border-gray-200 bg-white hover:border-gray-300 hover:shadow-lg transition-all duration-200">
                                  <div className="relative aspect-square bg-gray-100">
                                    {isImage(item) && item.publicUrl ? (
                                      <button type="button" className="absolute inset-0 w-full h-full block text-left focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 rounded-t-xl" onClick={() => { setSelectedImage(item.publicUrl); setShowImageModal(true); }}>
                                        <img src={item.publicUrl} alt={item.originalName || 'Media'} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                                          <span className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/95 text-gray-800 text-sm font-medium shadow">View</span>
                                        </div>
                                      </button>
                                    ) : isVideo(item) && item.publicUrl ? (
                                      <a href={item.publicUrl} target="_blank" rel="noopener noreferrer" className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300">
                                        <div className="w-14 h-14 rounded-full bg-white/90 shadow flex items-center justify-center group-hover:scale-110 transition-transform">
                                          <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-gray-700 ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                                        </div>
                                      </a>
                                    ) : (
                                      <a href={item.publicUrl || '#'} target="_blank" rel="noopener noreferrer" className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                                        <span className="mt-2 text-xs font-medium text-gray-600">Open file</span>
                                      </a>
                                    )}
                                  </div>
                                  <div className="p-3 border-t border-gray-100">
                                    <p className="text-sm font-medium text-gray-900 truncate" title={item.originalName}>{item.originalName || 'Unnamed file'}</p>
                                    <div className="flex flex-wrap gap-1.5 mt-2">
                                      {item.user_type && <span className="text-xs font-medium bg-gray-100 text-gray-700 px-2 py-0.5 rounded">{item.user_type}</span>}
                                    </div>
                                    <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                                      {item.createdAt && <span>{formatEasternDateTime(item.createdAt)}</span>}
                                      {fileSize(item) && <span>{fileSize(item)}</span>}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </CollapsibleSection>
          )}

          {/* ─── ORDER LOGS — TIMELINE ─── */}
          {order && order.order_logs && order.order_logs.length > 0 && (
            <CollapsibleSection
              title="Order logs"
              badge={order.order_logs.length}
              icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
              {...sectionCollapsible('order-logs')}
            >
              <div className="p-5 sm:p-6">
                <ol className="relative ml-3 border-l-2 border-gray-200 space-y-0">
                  {order.order_logs.map((log, idx) => {
                    const creator = getCreatorInfo(log.created_by);
                    return (
                      <li key={log.id} className="mb-6 ml-6 last:mb-0">
                        <span className="absolute -left-[11px] flex h-5 w-5 items-center justify-center rounded-full bg-white border-2 border-gray-200">
                          <span className={`w-2 h-2 rounded-full ${logDotClass(log.status)}`} />
                        </span>
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${logBadgeClass(log.status)}`}>
                            {log.status}
                          </span>
                          <time className="text-xs text-gray-400">{formatEasternDateTime(log.created_at)}</time>
                        </div>
                        {log.remark && <p className="text-sm text-gray-700 mb-0.5">{log.remark}</p>}
                        <p className="text-xs text-gray-400">
                          by <span className="text-gray-500 font-medium">{creator.name}</span>
                          {' '}<span className="text-gray-400">({creator.role})</span>
                        </p>
                      </li>
                    );
                  })}
                </ol>
              </div>
            </CollapsibleSection>
          )}

          {/* ─── PAYMENT INFORMATION ─── */}
          {order && (
            <CollapsibleSection
              title="Payment information"
              icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>}
              {...sectionCollapsible('payment')}
            >
              <div id="payment-information-section" className="p-5 sm:p-6">
                <OrderPaymentsLedgerPanel
                  orderId={id}
                  disabled={isCompleted}
                  openOfflinePaymentRequest={offlinePaymentRequest}
                  onOfflinePaymentRecorded={handleOfflinePaymentRecorded}
                />
              </div>
            </CollapsibleSection>
          )}

          {/* ─── REVIEW ─── */}
          {order && order.review && (
            <CollapsibleSection
              title="Review"
              icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>}
              {...sectionCollapsible('review')}
            >
              <div className="p-5 sm:p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* User review */}
                  <div className="bg-blue-50/50 rounded-xl border border-blue-100 p-4">
                    <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider mb-3">User review</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Submitted</span>
                        <span className={`font-semibold ${order.review.user_review_submitted ? 'text-emerald-700' : 'text-gray-400'}`}>
                          {order.review.user_review_submitted ? 'Yes' : 'No'}
                        </span>
                      </div>
                      {order.review.user_rating && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Rating</span>
                          <span className="font-bold text-amber-600">{order.review.user_rating} / 5 ★</span>
                        </div>
                      )}
                      {order.review.user_review && (
                        <p className="text-gray-700 pt-1 border-t border-blue-100">{order.review.user_review}</p>
                      )}
                      {order.review.user_review_date && (
                        <p className="text-xs text-gray-400">{formatEasternDateTime(order.review.user_review_date)}</p>
                      )}
                    </div>
                  </div>
                  {/* Contractor review */}
                  <div className="bg-green-50/50 rounded-xl border border-green-100 p-4">
                    <p className="text-xs font-semibold text-green-700 uppercase tracking-wider mb-3">Contractor review</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Submitted</span>
                        <span className={`font-semibold ${order.review.contractor_review_submitted ? 'text-emerald-700' : 'text-gray-400'}`}>
                          {order.review.contractor_review_submitted ? 'Yes' : 'No'}
                        </span>
                      </div>
                      {order.review.contractor_rating && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Rating</span>
                          <span className="font-bold text-amber-600">{order.review.contractor_rating} / 5 ★</span>
                        </div>
                      )}
                      {order.review.contractor_review && (
                        <p className="text-gray-700 pt-1 border-t border-green-100">{order.review.contractor_review}</p>
                      )}
                      {order.review.contractor_review_date && (
                        <p className="text-xs text-gray-400">{formatEasternDateTime(order.review.contractor_review_date)}</p>
                      )}
                    </div>
                  </div>
                </div>
                {order.review.expires_at && (
                  <p className="text-xs text-gray-400 mt-4">Review expires: {formatEasternDateTime(order.review.expires_at)}</p>
                )}
              </div>
            </CollapsibleSection>
          )}

          {/* ─── COMBO ─── */}
          {order && order.combo && (
            <CollapsibleSection
              title="Combo information"
              icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>}
              {...sectionCollapsible('combo')}
            >
              <div className="p-5 sm:p-6">
                <div className="bg-purple-50/50 rounded-xl border border-purple-100 p-4">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm mb-3">
                    <div><span className="text-gray-500 text-xs block mb-0.5">Title</span><span className="font-semibold text-gray-900">{order.combo.title}</span></div>
                    {order.combo.subtitle && <div><span className="text-gray-500 text-xs block mb-0.5">Subtitle</span><span className="font-semibold text-gray-900">{order.combo.subtitle}</span></div>}
                    <div><span className="text-gray-500 text-xs block mb-0.5">Discount</span><span className="font-bold text-purple-700">{order.combo.discount_percent}%</span></div>
                    <div><span className="text-gray-500 text-xs block mb-0.5">Final amount</span><span className="font-bold text-gray-900">{formatCentsAsCurrency(order.combo.final_amount, orderCurrency)}</span></div>
                    <div><span className="text-gray-500 text-xs block mb-0.5">Insurance</span><span className="font-semibold">{order.combo.insurance ? 'Yes' : 'No'}</span></div>
                    <div><span className="text-gray-500 text-xs block mb-0.5">Active</span><span className={`font-semibold ${order.combo.active ? 'text-emerald-700' : 'text-gray-500'}`}>{order.combo.active ? 'Yes' : 'No'}</span></div>
                  </div>
                  {order.combo.description && (
                    <p className="text-sm text-gray-600 pt-3 border-t border-purple-100">{order.combo.description}</p>
                  )}
                </div>
              </div>
            </CollapsibleSection>
          )}

          {/* ─── CONVERSATIONS ─── */}
          {order && order.conversation && order.conversation.length > 0 && (
            <CollapsibleSection
              title="Conversations"
              badge={order.conversation.length}
              icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>}
              {...sectionCollapsible('conversations')}
            >
              <div className="p-5 sm:p-6">
                <div className="space-y-3">
                  {order.conversation.map((conv) => (
                    <div key={conv.id} className="bg-gray-50 rounded-xl border border-gray-100 p-4">
                      <div className="flex flex-wrap items-center gap-3 mb-1">
                        <span className="text-sm font-semibold text-gray-700">{conv.type}</span>
                        <span className={`px-2.5 py-0.5 rounded-lg text-xs font-semibold ${
                          conv.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                          conv.status === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                          conv.status === 'IN_PROGRESS' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-700'
                        }`}>{conv.status}</span>
                        <span className="text-xs text-gray-400">{formatEasternDateTime(conv.createdAt)}</span>
                        {conv.lastMessageAt && (
                          <span className="text-xs text-gray-400">Last msg: {formatEasternDateTime(conv.lastMessageAt)}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CollapsibleSection>
          )}

        </div>
      </main>

      {/* ═══════════════════════════════════════
          MODALS
      ═══════════════════════════════════════ */}

      {/* Cancel Order */}
      <Dialog open={showCancelModal} onClose={() => setShowCancelModal(false)}>
        <div className="fixed inset-0 z-[120] bg-black/40 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto w-full max-w-sm rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className="h-1 w-full bg-gradient-to-r from-red-400 to-red-600" />
            <div className="p-6">
              <Dialog.Title className="text-lg font-bold text-gray-900 mb-4">Cancel Order</Dialog.Title>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Charge amount (dollars)</label>
                  <input type="number" className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                    value={cancelChargeAmount} onChange={(e) => setCancelChargeAmount(e.target.value)}
                    disabled={Number(cancelChargePercent) > 0} min="0" step="0.01" placeholder="e.g. 25.00" />
                  <p className="mt-1 text-xs text-gray-400">Enter dollars; the API stores cents.</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Charge percentage</label>
                  <input type="number" className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                    value={cancelChargePercent} onChange={(e) => setCancelChargePercent(Number(e.target.value))}
                    disabled={cancelChargeAmount !== ''} min="0" max="100" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Cancellation reason</label>
                  <textarea className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
                    rows={3} placeholder="Enter cancellation reason" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-5">
                <button className="px-4 py-2.5 rounded-xl bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 transition-colors"
                  onClick={() => { setShowCancelModal(false); setCancelReason(''); setCancelChargePercent(0); setCancelChargeAmount(''); }}>
                  Close
                </button>
                <button className="px-4 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-40 transition-colors"
                  onClick={handleCancelOrder}
                  disabled={
                    !cancelReason.trim() ||
                    isCancelling ||
                    (Number(cancelChargePercent) > 0 && cancelChargeAmount !== '') ||
                    (cancelChargeAmount !== '' && (Number.isNaN(Number(cancelChargeAmount)) || Number(cancelChargeAmount) < 0)) ||
                    (cancelChargeAmount === '' && (Number.isNaN(Number(cancelChargePercent)) || Number(cancelChargePercent) < 0 || Number(cancelChargePercent) > 100))
                  }>
                  {isCancelling ? (
                    <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Cancelling…</span>
                  ) : 'Confirm cancel'}
                </button>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Reschedule */}
      <Dialog open={showRescheduleModal} onClose={() => setShowRescheduleModal(false)}>
        <div className="fixed inset-0 z-[120] bg-black/40 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto w-full max-w-sm rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className="h-1 w-full bg-gradient-to-r from-amber-400 to-amber-600" />
            <div className="p-6">
              <Dialog.Title className="text-lg font-bold text-gray-900 mb-4">Reschedule Order</Dialog.Title>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">New date and time</label>
                  <DatePicker selected={rescheduleDate} onChange={setRescheduleDate} showTimeSelect dateFormat="Pp"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Charge percentage</label>
                  <input type="number" className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                    value={chargePercent} onChange={(e) => setChargePercent(Number(e.target.value))}
                    disabled={chargeAmount !== ''} min="0" max="100" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Charge amount (dollars)</label>
                  <input type="number" className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                    value={chargeAmount} onChange={(e) => setChargeAmount(e.target.value)}
                    disabled={Number(chargePercent) > 0} min="0" step="0.01" placeholder="e.g. 25.00" />
                  <p className="mt-1 text-xs text-gray-400">Enter dollars; the API stores cents.</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Reason</label>
                  <textarea className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                    rows={3} placeholder="Enter reschedule reason" value={rescheduleReason} onChange={(e) => setRescheduleReason(e.target.value)} />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-5">
                <button className="px-4 py-2.5 rounded-xl bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 transition-colors"
                  onClick={() => { setShowRescheduleModal(false); setRescheduleReason(''); setChargePercent(0); setChargeAmount(''); }}>
                  Close
                </button>
                <button className="px-4 py-2.5 rounded-xl bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 disabled:opacity-40 transition-colors"
                  onClick={handleRescheduleOrder}
                  disabled={
                    !rescheduleReason.trim() ||
                    isRescheduling ||
                    (Number(chargePercent) > 0 && chargeAmount !== '') ||
                    (chargeAmount !== '' && (Number.isNaN(Number(chargeAmount)) || Number(chargeAmount) < 0)) ||
                    (chargeAmount === '' && (Number.isNaN(Number(chargePercent)) || Number(chargePercent) < 0 || Number(chargePercent) > 100))
                  }>
                  {isRescheduling ? (
                    <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Rescheduling…</span>
                  ) : 'Confirm reschedule'}
                </button>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Assign Contractor */}
      <Dialog open={showAssignModal} onClose={() => setShowAssignModal(false)}>
        <div className="fixed inset-0 z-[120] bg-black/40 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className="h-1 w-full bg-gradient-to-r from-emerald-400 to-emerald-600" />
            <div className="p-6">
              <Dialog.Title className="text-lg font-bold text-gray-900 mb-4">Assign Contractor</Dialog.Title>
              <input type="text" placeholder="Search by name or mobile…"
                className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 mb-3"
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
              <div className="max-h-96 overflow-y-auto -mx-1 px-1 space-y-1">
                {getFilteredContractors(contractors, searchTerm).map((contractor) => (
                  <div key={contractor.contractor_id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 border border-transparent hover:border-gray-200 transition-all">
                    <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-sm shrink-0">
                      {contractor.user.name?.charAt(0)?.toUpperCase() || 'C'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{contractor.user.name}</p>
                      <p className="text-xs text-gray-500 font-mono">{contractor.user.mobile}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-gray-500">Commission: <span className="font-semibold">{formatCommissionLabel(contractor.commission_percentage)}</span></span>
                        <button type="button" onClick={() => openCommissionEditor(contractor)}
                          className="text-xs text-blue-600 underline hover:text-blue-800">Edit</button>
                      </div>
                    </div>
                    <button onClick={() => handleAssignContractor(contractor.contractor_id)} disabled={isAssigning}
                      className="shrink-0 px-3.5 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 transition-colors">
                      {isAssigning ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin inline-block" /> : 'Assign'}
                    </button>
                  </div>
                ))}
                {getFilteredContractors(contractors, searchTerm).length === 0 && (
                  <p className="text-center py-8 text-sm text-gray-400">No contractors found</p>
                )}
              </div>
              <div className="flex justify-end mt-4">
                <button className="px-4 py-2.5 rounded-xl bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 transition-colors"
                  onClick={() => setShowAssignModal(false)}>Close</button>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Commission editor */}
      <Dialog open={Boolean(commissionEditContractor)} onClose={() => { if (!isSavingCommission) setCommissionEditContractor(null); }}>
        <div className="fixed inset-0 z-[130] bg-black/40 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto w-full max-w-sm rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className="h-1 w-full bg-gradient-to-r from-blue-400 to-blue-600" />
            <div className="p-6">
              <Dialog.Title className="text-lg font-bold text-gray-900 mb-1">Update commission</Dialog.Title>
              {commissionEditContractor?.user?.name && (
                <p className="text-sm text-gray-500 mb-4">{commissionEditContractor.user.name}</p>
              )}
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Commission percentage (0–100)</label>
              <input type="number" min={0} max={100} step="0.01"
                className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 mb-5"
                value={commissionInput} onChange={(e) => setCommissionInput(e.target.value)} disabled={isSavingCommission} />
              <div className="flex justify-end gap-3">
                <button type="button" className="px-4 py-2.5 rounded-xl bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 transition-colors"
                  disabled={isSavingCommission} onClick={() => setCommissionEditContractor(null)}>Cancel</button>
                <button type="button" className="px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 transition-colors"
                  disabled={isSavingCommission} onClick={handleSaveCommission}>
                  {isSavingCommission ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Notify Contractors */}
      <Dialog open={showNotifyModal} onClose={() => setShowNotifyModal(false)}>
        <div className="fixed inset-0 z-[120] bg-black/40 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className="h-1 w-full bg-gradient-to-r from-blue-400 to-blue-600" />
            <div className="p-6">
              <Dialog.Title className="text-lg font-bold text-gray-900 mb-4">Notify Contractors</Dialog.Title>
              <input type="text" placeholder="Search by name or mobile…"
                className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 mb-3"
                value={notifySearchTerm} onChange={(e) => setNotifySearchTerm(e.target.value)} />
              <div className="max-h-80 overflow-y-auto -mx-1 px-1 space-y-1">
                {getFilteredContractors(contractors, notifySearchTerm).map((contractor) => (
                  <label key={contractor.contractor_id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 cursor-pointer">
                    <input type="checkbox" className="w-4 h-4 rounded accent-blue-600"
                      checked={selectedContractors.includes(contractor.contractor_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedContractors([...selectedContractors, contractor.contractor_id]);
                        } else {
                          setSelectedContractors(selectedContractors.filter(cid => cid !== contractor.contractor_id));
                        }
                      }} />
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-xs shrink-0">
                      {contractor.user.name?.charAt(0)?.toUpperCase() || 'C'}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{contractor.user.name}</p>
                      <p className="text-xs text-gray-500 font-mono">{contractor.user.mobile}</p>
                    </div>
                  </label>
                ))}
                {getFilteredContractors(contractors, notifySearchTerm).length === 0 && (
                  <p className="text-center py-8 text-sm text-gray-400">No contractors found</p>
                )}
              </div>
              <div className="flex justify-end gap-3 mt-4">
                <button className="px-4 py-2.5 rounded-xl bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 transition-colors"
                  onClick={() => { setShowNotifyModal(false); setSelectedContractors([]); setNotifySearchTerm(''); }}>
                  Cancel
                </button>
                <button className="px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 transition-colors"
                  onClick={handleNotifyContractors} disabled={selectedContractors.length === 0 || isNotifying}>
                  {isNotifying ? (
                    <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Notifying…</span>
                  ) : `Notify selected (${selectedContractors.length})`}
                </button>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Unassign */}
      <Dialog open={showUnassignModal} onClose={() => setShowUnassignModal(false)}>
        <div className="fixed inset-0 z-[120] bg-black/40 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto w-full max-w-sm rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className="h-1 w-full bg-gradient-to-r from-orange-400 to-orange-600" />
            <div className="p-6">
              <Dialog.Title className="text-lg font-bold text-gray-900 mb-2">Unassign contractor?</Dialog.Title>
              <p className="text-sm text-gray-500 mb-5">This will remove the assigned contractor from this order. Are you sure?</p>
              <div className="flex justify-end gap-3">
                <button className="px-4 py-2.5 rounded-xl bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 transition-colors"
                  onClick={() => setShowUnassignModal(false)}>Cancel</button>
                <button className="px-4 py-2.5 rounded-xl bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 disabled:opacity-40 transition-colors"
                  onClick={() => { handleUnassignContractor(); setShowUnassignModal(false); }} disabled={isUnassigning}>
                  {isUnassigning ? (
                    <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Unassigning…</span>
                  ) : 'Confirm unassign'}
                </button>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Image preview */}
      <Dialog open={showImageModal} onClose={() => setShowImageModal(false)}>
        <div className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-5xl w-full rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className="relative p-2 sm:p-4">
              <button onClick={() => setShowImageModal(false)}
                className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/90 text-gray-700 hover:bg-gray-100 shadow-md transition-colors" aria-label="Close">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              {selectedImage && (
                <img src={selectedImage} alt="Enlarged view" className="max-h-[85vh] w-auto mx-auto rounded-lg object-contain" />
              )}
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Approve charge */}
      <Dialog open={showApproveChargeModal} onClose={() => setShowApproveChargeModal(false)}>
        <div className="fixed inset-0 z-[120] bg-black/40 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto w-full max-w-sm rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className="h-1 w-full bg-gradient-to-r from-emerald-400 to-emerald-600" />
            <div className="p-6">
              <Dialog.Title className="text-lg font-bold text-gray-900 mb-4">Approve charge</Dialog.Title>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Admin remark</label>
              <textarea className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-none mb-5"
                rows={3} placeholder="Enter admin remark" value={adminRemark} onChange={(e) => setAdminRemark(e.target.value)} />
              <div className="flex justify-end gap-3">
                <button className="px-4 py-2.5 rounded-xl bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 transition-colors"
                  onClick={() => setShowApproveChargeModal(false)}>Cancel</button>
                <button className="px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40 transition-colors"
                  onClick={handleApproveCharge} disabled={!adminRemark.trim() || isApprovingCharge}>
                  {isApprovingCharge ? (
                    <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Approving…</span>
                  ) : 'Confirm approve'}
                </button>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Reject charge */}
      <Dialog open={showRejectChargeModal} onClose={() => setShowRejectChargeModal(false)}>
        <div className="fixed inset-0 z-[120] bg-black/40 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto w-full max-w-sm rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className="h-1 w-full bg-gradient-to-r from-red-400 to-red-600" />
            <div className="p-6">
              <Dialog.Title className="text-lg font-bold text-gray-900 mb-4">Reject charge</Dialog.Title>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Admin remark</label>
              <textarea className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none mb-5"
                rows={3} placeholder="Enter admin remark" value={adminRemark} onChange={(e) => setAdminRemark(e.target.value)} />
              <div className="flex justify-end gap-3">
                <button className="px-4 py-2.5 rounded-xl bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 transition-colors"
                  onClick={() => setShowRejectChargeModal(false)}>Cancel</button>
                <button className="px-4 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-40 transition-colors"
                  onClick={handleRejectCharge} disabled={!adminRemark.trim() || isRejectingCharge}>
                  {isRejectingCharge ? (
                    <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Rejecting…</span>
                  ) : 'Confirm reject'}
                </button>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

    </div>
  );
}

export default OrderDetails;
